import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { IJob, IJobForm } from 'core/interface/job';
import { CompetencieService } from 'services/competencies/competencies';
import useLocales from 'hooks/useLocales';
import Button from 'components/Button/Index';
import Popup from 'components/shared/Popups/Popup/Index';
import NewJobPopup from 'components/shared/Popups/NewJobPopup/Index';
import CompetenciesSelect from 'components/shared/CompetenciesSelect';
import MatchingProfileBlock from 'components/MatchingProfileBlock/Index';
import NoData from 'components/shared/NoData/Index';
import { JobService } from 'services/jobs/job';
import { COMPANY_ROUTES } from 'core/constants/companyRoutes';
import { RootState } from '../../../redux/store';
import {
  industriesInputToApiPayload,
  jobLocationsToApiPayload
} from 'utils/jobIndustriesLocations';
import { pickMusts } from 'utils/musts';
import CompetenciesPopup from 'components/shared/Popups/CompetenciesPopup/Index';
import { getCompetenciesSelectFooter } from 'components/shared/CompetenciesSelect';
import Pagination from '@material-ui/core/Pagination';
import LinkIcon from '@mui/icons-material/Link';
import CompanyJobDetail from 'app/companyJobDetail/Index';

import './Index.scss';

const PostAJob: FC = (): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [ids, setIds] = useState<string[]>([]);
  const { jobs } = useSelector((state: RootState) => state.jobs);
  const { industry: industryCatalog } = useSelector(
    (state: RootState) => state.industries
  );
  const [showJobIndustriesPopup, setShowJobIndustriesPopup] =
    useState<boolean>(false);
  const [firstStepData, setFirstStepData] = useState<IJobForm | number[]>();
  const [selectedJobId, setSelectedJobId] = useState<any>();
  const [selectedJob, setSelectedJob] = useState<IJob | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [step, setStep] = useState(1);
  const [scores, setScores] = useState<{ [label: string]: number }>({});
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const { assessmentByGroup } = useSelector(
    (state: RootState) => state.assessment
  );

  const handleOpenPopup = (): void => {
    setShowPopup(true);
    setIsEditing(false);
    setSelectedJobId('');
    setSelectedJob(null);
  };

  const getAssessment = () => {
    JobService.getAssessmentByGroup();
  };

  const getApplications = async (): Promise<void> => {
    setLoading(true);
    try {
      await JobService.getJobs({ limit, offset });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const togglePopUp = (): void => {
    const closing = showPopup;
    setShowPopup(!showPopup);
    if (closing) {
      setIsEditing(false);
      setSelectedJobId('');
      setSelectedJob(null);
      getApplications();
    }
  };

  const toggleMatchPopUp = (): void => {
    setShowJobIndustriesPopup(!showJobIndustriesPopup);
    setStep(1);
  };

  useEffect(() => {
    getCompetencies();
    getApplications();
    getAssessment();
  }, [limit, offset]);

  useEffect(() => {
    const foundJob = jobs?.results?.find(
      (profile: IJob) => profile.id === selectedJobId
    );

    if (foundJob) {
      setSelectedJob(foundJob);
    }
  }, [jobs, selectedJobId]);

  useEffect(() => {
    if (jobs && jobs.count !== undefined) {
      setTotalCount(jobs.count);
    } else if (jobs && jobs.results) {
      setTotalCount(jobs.results.length);
    }
  }, [jobs]);

  useEffect(() => {
    setPage(1);
  }, [jobs]);

  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);

  const handleSetSelected = (label: string, id: string | number) => {
    const normalizedId = String(id);
    const labelIndex = selectedLabels.indexOf(label);

    if (labelIndex !== -1) {
      setSelectedLabels(selectedLabels.filter((_, i) => i !== labelIndex));
      setIds(ids.filter((_, i) => i !== labelIndex));
      return;
    }

    if (selectedLabels.length >= 10) {
      return;
    }

    setSelectedLabels([...selectedLabels, label]);
    setIds([...ids, normalizedId]);
  };

  const handleDelete = (id: number) => {
    JobService.deleteJobByIdOrSlug(id);
  };

  const handleNextStep = (
    evt?: IJobForm | number[],
    aiCompetencies?: { ids: string[]; labels: string[] }
  ): void => {
    evt && setFirstStepData(evt);
    if (aiCompetencies) {
      setSelectedLabels(aiCompetencies.labels);
      setIds(aiCompetencies.ids);
    }
    togglePopUp();
    setShowJobIndustriesPopup(!showJobIndustriesPopup);
  };

  const handleCompetenciesNextStep = () => {
    if (selectedLabels.length >= 7 && selectedLabels.length <= 10) {
      const idLabelArray = ids.map((id, index) => ({
        id,
        competency: selectedLabels[index]
      }));

      // console.log(selectedJob, selectedJob?.competency_min_scores, selectedJobId, idLabelArray, "score");
      const existingLabels = selectedJob?.competency_labels || [];
      const competencyScores = selectedJob?.competency_min_scores || [];
      const data = {
        firstStepData,
        competencies: idLabelArray,
        edit: selectedJobId ?? false,
        scores: idLabelArray.map(({ competency }) =>
          existingLabels.includes(competency)
            ? competencyScores[existingLabels.indexOf(competency)]
            : 0
        ),
        postAJob: true
      };
      localStorage.setItem('selected', JSON.stringify(data));
      navigate('/company/culture');
    }
  };

  const getCompetencies = (): void => {
    CompetencieService.getCompetencies();
  };

  const getJobApplication = (id: number) => {
    // previously: navigate(`/company/find-candidate/${id}`);
    navigate(COMPANY_ROUTES.jobPostRequests(id));
  };

  const getJobList = (id: number) => {
    navigate(COMPANY_ROUTES.jobPostFind(id));
  };

  useEffect(() => {
    if (selectedJob?.id) {
      setSelectedLabels(selectedJob?.competency_labels);
      const a = selectedJob?.competencies?.map((e: any) =>
        String(e?.competency?.id)
      );
      setIds(a);
    } else {
      setSelectedLabels([]);
      setIds([]);
    }
  }, [selectedJob]);

  const handleSavePopup = async (
    selectedLabels: string[],
    scores: Record<string, number>,
    formValues: any
  ): Promise<void> => {
    const allCompetencies = assessmentByGroup.flatMap(
      (group: any) => group.competencies
    );
    const nameToId: Record<string, number> = {};
    allCompetencies.forEach((c: any) => {
      nameToId[c.name] = c.id;
    });

    // Helper to get value from formValues, then firstStepData, then fallback
    const getValue = (key: string, fallback: any = undefined) => {
      if (
        formValues &&
        formValues[key] !== undefined &&
        formValues[key] !== null
      )
        return formValues[key];
      if (
        firstStepData &&
        typeof firstStepData === 'object' &&
        !Array.isArray(firstStepData) &&
        (firstStepData as any)[key] !== undefined &&
        (firstStepData as any)[key] !== null
      )
        return (firstStepData as any)[key];
      return fallback;
    };

    const musts = pickMusts(
      getValue('musts', null),
      getValue('requirements', null)
    );

    // Ensure skills are sent as ESCO/custom payload objects
    let skills = getValue('skills', []);
    if (!Array.isArray(skills) || skills === null) skills = [];
    let industries = getValue('industries', []);
    if (!Array.isArray(industries) || industries === null) industries = [];
    industries = industriesInputToApiPayload(
      industries,
      industryCatalog || []
    );

    // Format locations as an array of location objects with id and display_name
    let locations = getValue('locations', []);
    if (!Array.isArray(locations) || locations === null) locations = [];

    locations = jobLocationsToApiPayload(
      locations
        .map((loc: any) => {
          if (typeof loc === 'object' && loc !== null) {
            return {
              id: loc.id || -1,
              display_name: loc.display_name || loc.title || loc.name || ''
            };
          }
          return { id: -1, display_name: '' };
        })
        .filter((loc: any) => loc.id !== -1)
    );

    const cultureProfileValue = getValue('culture_profile', null);
    let cultureProfileId: number | null = null;
    if (cultureProfileValue != null) {
      if (typeof cultureProfileValue === 'number') {
        cultureProfileId = cultureProfileValue > 0 ? cultureProfileValue : null;
      } else if (
        typeof cultureProfileValue === 'object' &&
        'id' in cultureProfileValue
      ) {
        const id = (cultureProfileValue as { id?: number }).id;
        cultureProfileId = id != null && id > 0 ? id : null;
      }
    }

    // Compose the request body with all required fields
    // Note: Some field names are different between the form and the API:
    // - form `musts` (legacy: `requirements`) maps to API `musts`
    // - 'required_experience' in the form becomes 'work_exp' for the API
    const reqBody = {
      title: getValue('job_title', ''),
      description: getValue('job_description', ''),
      is_public: Boolean(getValue('is_public', false)),
      degree: Number(getValue('degree', 0)),
      locations,
      industries,
      musts,
      skills,
      work_exp: Number(getValue('required_experience', 0)), // Form field: required_experience -> API field: work_exp
      prefered_salary: Number(getValue('prefered_salary', 0)),
      competencies: selectedLabels.map((label: string) => ({
        competency: nameToId[label],
        min_score: scores[label] ?? 5
      })),
      culture_profile: cultureProfileId,
      if_office_work: Boolean(getValue('if_office_work', false)),
      is_remote: Boolean(getValue('is_remote', false)),
      status: String(getValue('status', '')),
      contract: Number(getValue('contract_type', 0)),
      compensation: Number(getValue('compensation', 0))
    };

    try {
      if (isEditing && selectedJobId) {
        await JobService.saveJobEdit((firstStepData as any).edit, reqBody);
        setIsEditing(false);
      } else {
        await JobService.saveJob(reqBody);
      }

      if (page !== 1) {
        setPage(1);
      } else {
        setLoading(true);
        try {
          await JobService.getJobs({ limit, offset: 0 });
        } finally {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error saving job:', error);
    } finally {
      await getApplications();
      setShowJobIndustriesPopup(false);
      setStep(1);
    }
  };

  return (
    <div className="postjob">
      <div className="postjob__header">
        <span className="postjob__header-title">{t('Job Posts')}</span>
        <Button
          onClick={handleOpenPopup}
          t={t}
          title={t('Create New Job Post')}
          className="postjob__header-btn"
        />
      </div>
      {loading ? (
        <div className="loadingContainer">
          <CircularProgress className="loading" />
        </div>
      ) : jobs?.results?.length === 0 ? (
        <NoData />
      ) : (
        <>
          <div className="postjob_blocks">
            {jobs?.results?.map((el: any) => (
              <MatchingProfileBlock
                id={el.id}
                title={el.title}
                name={''}
                date={el.last_competencies_updated_on}
                key={el.id}
                setEditPopUp={() => {
                  setIsEditing(true);
                  togglePopUp();
                }}
                setDeletedItem={handleDelete}
                setSelectedMatchProfileId={setSelectedJobId}
                getOnePost={getJobApplication}
                getList={getJobList}
                type="job-posts"
                createdBy={el?.user || ''}
                onTitleClick={() => navigate(COMPANY_ROUTES.jobPost(el.id))}
              />
            ))}
          </div>
          <div
            style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}
          >
            <Pagination
              className="pagination"
              count={Math.ceil((jobs?.count || 0) / limit)}
              page={page}
              onChange={(_, value) => setPage(value)}
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </div>
        </>
      )}

      {showPopup && (
        <Popup
          title={t(
            `${isEditing ? 'Edit your job post' : 'Create your new job post'} `
          )}
          description={
            isEditing
              ? undefined
              : "Please let people know what's important for your job opening"
          }
          onClose={togglePopUp}
          className={isEditing ? 'popup--job-detail' : undefined}
          titleAction={
            isEditing && selectedJobId ? (
              <button
                type="button"
                className="popup__title-link"
                onClick={() => navigate(COMPANY_ROUTES.jobPost(selectedJobId))}
                aria-label={t('Open job detail page')}
              >
                <LinkIcon fontSize="small" />
              </button>
            ) : undefined
          }
        >
          {isEditing && selectedJobId ? (
            <CompanyJobDetail jobId={selectedJobId} embedded />
          ) : (
            <NewJobPopup nextStep={handleNextStep} selectedJob={selectedJob} />
          )}
        </Popup>
      )}
      {showJobIndustriesPopup && (
        <CompetenciesPopup
          className="popup-add-new"
          title={t('Create your new job post')}
          description="Please select the most important competencies for your job opening."
          onClose={toggleMatchPopUp}
          footer={getCompetenciesSelectFooter({
            step,
            t,
            handleBack: () => setStep(1),
            handleSave: () =>
              handleSavePopup(selectedLabels, scores, firstStepData),
            handleNext: () => setStep(2),
            selectedList: selectedLabels,
            disabled: selectedLabels.length < 7 || selectedLabels.length > 10
          })}
        >
          <CompetenciesSelect
            step={step}
            setStep={setStep}
            competencies={assessmentByGroup}
            selectedList={selectedLabels}
            onSelect={handleSetSelected}
            matchProfile={false}
            scores={scores}
            setScores={setScores}
          />
        </CompetenciesPopup>
      )}
    </div>
  );
};

export default PostAJob;
